import { useMemo, useCallback, memo, useState, useRef } from 'react'
import { flatJoin, numberFormat } from 'shared/util'

import { KojinResult } from 'interface/kojin'
import { AuthorLinkResult } from 'interface/author'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { GraphChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import { TooltipComponent, LegendComponent } from 'echarts/components'

import { AuthorGraphCategory, AuthorGraphType, SymbolSizeRuleType } from './types'
import { AuthorGraphOptionBar } from './optionBar'
import { AuthorGraphSliderBar } from './sliderBar'

echarts.use([TooltipComponent, LegendComponent, GraphChart, CanvasRenderer])

type AuthorGraphNodeValue = {
  kojinCode?: string
  authorId: number
  count?: number
  rank?: number
  line?: string
}
type AuthorGraphNode = {
  id: string
  name?: string
  symbol?: string
  symbolSize: number
  symbolKeepAspect: boolean
  value: AuthorGraphNodeValue
  category?: number
}
type AuthorCatgory = {
  code: string
  name: string
  count: number
}
type GraphLink = {
  id: string
  source: string
  target: string
  emphasis: {
    disabled: boolean
  }
}

interface AuthorGraphProps {
  kojinList: KojinResult[] | null
  linkList: AuthorLinkResult[] | null
  onSelectKojin: (kojinCode: string | null) => void
  onSelectAuthor: (authorId: number | null) => void
  useActor?: boolean
}
const AuthorGraphCore = ({
  kojinList,
  linkList,
  onSelectKojin,
  onSelectAuthor,
  useActor
}: AuthorGraphProps) => {
  const echartsRef = useRef<ReactEChartsCore>(null)
	const [limit, setLimit] = useState<number>(100)
	const [showSingleNode, setShowSingleNode] = useState<boolean>(false)
	const [category, setCategory] = useState<AuthorGraphCategory>('sotukou')
	const [graphType, setGraphType] = useState<AuthorGraphType>('default')
  const [symbolSizeRule, setSymbolSizeRule] = useState<SymbolSizeRuleType>('score')

  const getCategory = useCallback(
    (kojin: KojinResult) => {
      if (category === 'sotukou') {
        return (kojin.sotukouCode && kojin.sotukouCode !== '') ? (
          { code: kojin.sotukouCode, name: kojin.sotukouName }
         ) : null
      } else if (category === 'isikai') {
        return (kojin.isikaiCode && kojin.isikaiCode !== '') ? (
          { code: kojin.isikaiCode, name: kojin.isikaiName }
         ) : null
      } else if (category === 'nenrei') {
        return kojin.nenrei ? (
          kojin.nenrei < 20 ? { code: '10', name: '10代以下' } :
          kojin.nenrei < 30 ? { code: '20', name: '20代' } :
          kojin.nenrei < 40 ? { code: '30', name: '30代' } :
          kojin.nenrei < 50 ? { code: '40', name: '40代' } :
          kojin.nenrei < 60 ? { code: '50', name: '50代' } :
          kojin.nenrei < 70 ? { code: '60', name: '60代' } :
          kojin.nenrei < 80 ? { code: '70', name: '70代' } : { code: '80', name: '80歳以上' }
        ) : null
      } else if (category === 'seibetu') {
        return (
          kojin.seibetuCode === '1' ? { code: kojin.seibetuCode, name: '男性' } :
          kojin.seibetuCode === '2' ? { code: kojin.seibetuCode, name: '女性' } : null
        )
      } else if (category === 'sinryoka') {
        return kojin.primarySinryoka ? {
          code: kojin.primarySinryoka.sinryokaCode,
          name: kojin.primarySinryoka.sinryokaName ?? ''
        } : null
      } else if (category === 'kanriBunrui') {
        return (
          kojin.kanriBunrui === 'D01' ? { code: kojin.kanriBunrui, name: 'D01:診療所 経営者' } :
          kojin.kanriBunrui === 'D02' ? { code: kojin.kanriBunrui, name: 'D02:診療所 勤務医' } :
          kojin.kanriBunrui === 'D03' ? { code: kojin.kanriBunrui, name: 'D03:小規模病院' } : 
          kojin.kanriBunrui === 'D04' ? { code: kojin.kanriBunrui, name: 'D04:中規模病院' } :
          kojin.kanriBunrui === 'D05' ? { code: kojin.kanriBunrui, name: 'D05:大病院' } :
          kojin.kanriBunrui === 'D06' ? { code: kojin.kanriBunrui, name: 'D06:大規模病院' } :
          kojin.kanriBunrui === 'D07' ? { code: kojin.kanriBunrui, name: 'D07:その他' } :
          kojin.kanriBunrui === 'P01' ? { code: kojin.kanriBunrui, name: 'P01:病院 薬剤師' } :
          kojin.kanriBunrui === 'P02' ? { code: kojin.kanriBunrui, name: 'P02:診療所 薬剤師' } :
          kojin.kanriBunrui === 'P03' ? { code: kojin.kanriBunrui, name: 'P03:調剤薬局 薬剤師' } :
          kojin.kanriBunrui === 'P04' ? { code: kojin.kanriBunrui, name: 'P04:その他 薬剤師' } : null
        )
      } else if (category === 'kaikin') {
        return (
          kojin.kaikinCode === '1' ? { code: kojin.kaikinCode, name: '開業医' } :
          kojin.kaikinCode === '2' ? { code: kojin.kaikinCode, name: '勤務医' } : null
        )
      } else if (category === 'tierRank') {
        return kojin.mrpStats ? kojin.mrpStats.rank : { code: 'Z', name: '非会員' }
      }
    },
    [category]
  )

  const graph = useMemo<{
    nodes: AuthorGraphNode[]
    graphLinks: GraphLink[]
    categories: AuthorCatgory[]
    legend: string[]
    edgeLength: number
  } | null>(
    () => {
      if (!kojinList || kojinList.length === 0) return null
      if (!linkList || linkList.length === 0) return null

      const limitedKojinList = kojinList.slice(0, limit)
      const authorMap: {
        [index: number]: {
          kojin: KojinResult
          score: number
          articleIdList: number[]
          collaboList: number[]
          linkCount: number
        }
      } = {}
      const authorIdList: number[] = []

      const articleMap: { [index: number]: { authorIdList: number[] }} = {}
      const articleIdList: number[] = []
      const categories: AuthorCatgory[] = []
      let noCategoryCount = 0
      let scoreMin = undefined
      let scoreMax = undefined
      let linkCountMax = undefined

      for (const link of linkList) {
        let author = authorMap[link.authorId]
        if (!author) {
          const kojin = limitedKojinList.find(kojin => kojin.authorId === link.authorId)
          if (!kojin) continue

          const score = kojin.score?.journal?.score ?? 0
          author = {
            kojin,
            score,
            articleIdList: [],
            collaboList: [],
            linkCount: 0
          }
          authorMap[link.authorId] = author
          authorIdList.push(link.authorId)
          if (scoreMin === undefined || scoreMin > score) scoreMin = score
          if (scoreMax === undefined || scoreMax < score) scoreMax = score

          const ctg = getCategory(kojin)
          if (ctg && ctg.code && ctg.name) {
            const category = categories.find(c => c.code === ctg.code)
            if (category) {
              category.count = category.count + 1
            } else {
              categories.push({
                code: ctg.code,
                name: ctg.name,
                count: 1
              })
            }
          } else {
            noCategoryCount++
          }
        }
        author.articleIdList.push(link.articleId)

        let article = articleMap[link.articleId]
        if (!article) {
          article = { authorIdList: [] }
          articleMap[link.articleId] = article
          articleIdList.push(link.articleId)
        } else {
          for (const collaboId of article.authorIdList) {
            if (collaboId === link.authorId) continue
            const collaboIndex = author.collaboList.findIndex(collaboAuthorId => collaboAuthorId === collaboId)
            if (collaboIndex >= 0) continue

            const collabo = authorMap[collaboId]
            if (!collabo) continue

            const linkIndex = collabo.collaboList.findIndex(collaboAuthorId => collaboAuthorId === link.authorId)
            if (linkIndex >= 0) continue

            collabo.collaboList.push(link.authorId)
            collabo.linkCount = collabo.linkCount + 1
            author.linkCount = author.linkCount + 1
            if (linkCountMax === undefined || linkCountMax < author.linkCount) linkCountMax = author.linkCount
          }
        }
        article.authorIdList.push(link.authorId)
      }
      categories.sort((a, b) => b.count - a.count)
      if (noCategoryCount > 0) {
        categories.unshift({
          code: '@',
          name: '不明',
          count: noCategoryCount
        })
      }
      const nodes: AuthorGraphNode[] = []
      const graphLinks: GraphLink[] = []
      const scoreMinSafe = scoreMin ?? 0
      const scoreMaxSafe = scoreMax ?? 0
      const linkCountMaxSafe = linkCountMax ?? 1
      const symbolSizeMin = 8
      const symbolSizeMax = 30

      for (const authorId of authorIdList) {
        const author = authorMap[authorId]
        if (!showSingleNode && author.linkCount === 0) continue

        for (const collaboId of author.collaboList) {
          graphLinks.push({
            id: `${authorId}-${collaboId}`,
            source: authorId.toString(),
            target: collaboId.toString(),
            emphasis: {
              disabled: false,
            }
          })
        }
        let categoryIndex = undefined
        const ctg = getCategory(author.kojin)
        if (ctg && ctg.code && ctg.name) {
          categoryIndex = categories.findIndex(c => c.code === ctg.code)
          if (categoryIndex < 0) categoryIndex = undefined
        } else {
          categoryIndex = 0
        }
        const infos = []
        if (author.kojin.sotukouName) infos.push(author.kojin.sotukouName)
        if (author.kojin.nenrei) infos.push(`${author.kojin.nenrei}歳`)
        if (author.kojin.seibetuCode === '1') infos.push('男性')
        else if (author.kojin.seibetuCode === '2') infos.push('女性')

        const symbolSize = (
          (symbolSizeRule === 'link' && linkCountMaxSafe > 0) ? (
            ((symbolSizeMax * author.linkCount) / linkCountMaxSafe) + symbolSizeMin
          ) : 
          (symbolSizeRule === 'score' && scoreMaxSafe > scoreMinSafe)? (
            ((symbolSizeMax * ((author?.kojin.score?.journal?.score ?? 0) - scoreMinSafe)) / (scoreMaxSafe - scoreMinSafe)) + symbolSizeMin
          ) : (
            symbolSizeMin
          )
        )
        nodes.push({
          id: authorId.toString(),
          name: author.kojin.isiName ?? author.kojin.isiNameKana ?? '(no name)',
          symbol: useActor === true ? (
            author.kojin.actor?.photoLink ? `image://${author.kojin.actor.photoLink}` : undefined
          ) : undefined,
          symbolSize,
          symbolKeepAspect: true,
          value: {
            kojinCode: author.kojin?.kojinCode ?? undefined,
            authorId: authorId,
            count: author.kojin.score?.journal?.counts.total,
            rank: author.kojin.score?.journal?.rank ?? undefined,
            line: infos.join(' ')
          },
          category: categoryIndex
        })
      }
      return {
        nodes,
        graphLinks,
        categories,
        legend: categories.map(category => category.name),
        edgeLength: /*100*/(linkCountMaxSafe / 5) + 60
      }
    },
    [limit, kojinList, linkList, showSingleNode, getCategory, useActor, symbolSizeRule]
  )

  const toolTipFormatter = useCallback(
    (obj: {
      value: AuthorGraphNodeValue,
      name: string
    }) => {
      if (!obj.value) return undefined
      const value = obj.value as AuthorGraphNodeValue
      return flatJoin('<br>',
        obj.name,
        value.line,
        (value.rank || value.count) ? `論文 ${numberFormat(value.rank, '-')}位 / ${numberFormat(value.count, '-')}件` : null
      )
    },
    []
  )

  const option = useMemo(
    () => graph ? {
      tooltip: {
        formatter: toolTipFormatter
      },
      legend: {
        data: graph.legend,
        type: 'scroll',
        orient: 'vertical',
        backgroundColor: 'rgba(255,255,255,0.5)',
        right: 10,
        top: 10 + 64,
        bottom: 10,
        selector: [{ type: 'all', title: '全選択' }]
      },
      series: [{
        type: 'graph',
        data: graph.nodes,
        links: graph.graphLinks,
        categories: graph.categories,
        legendHoverLink: false,
        //animation: false,
        roam: true,
        roamTrigger: 'global',
        symbolKeepAspect: true,
        label: {
          show: true,
          position: 'bottom',
          formatter: '{b}',
        },
        labelLayout: {
          hideOverlap: false
        },
        lineStyle: {
          color: 'source',
          curveness: 0.3
        },
        emphasis: {
          focus: 'adjacency',
          scale: false,
          lineStyle: {
            borderWidth: 10
          }
        },
        layout: graphType === 'circular' ? 'circular' : 'force',
        draggable: graphType === 'circular' ? false : true,
        force: graphType === 'default' ? {
          edgeLength: graph.edgeLength,
          repulsion: 50,
          gravity: 0.1,
          friction: 0.2,
          layoutAnimation: true
        } : undefined,
        circular: graphType === 'circular' ? {
          rotateLabel: true
        } : undefined
      }],
      textStyle: {
        fontFamily: 'Noto Sans JP,sans-serif',
      }
    } : undefined,
    [graph, graphType, toolTipFormatter]
  )
  const onClickGraph = useCallback(
    (obj: {
      value: { kojinCode: string, authorId: number }
    }) => {
      if (!obj.value) return
      const value = obj.value
      if (value.kojinCode) {
        onSelectKojin(value.kojinCode)
      } else {
        onSelectAuthor(value.authorId)
      }
    },
    [onSelectKojin, onSelectAuthor]
  )
  const onLegendSelectChanged = useCallback(
    (params: {
      name: string
      selected: { [key: string]: boolean }
    }) => {
      const { name, selected } = params
      if (Object.entries(selected).every(
        ([key, sel]) => key === name ? sel === false : sel
      )) {
        // 全選択から任意の項目を選択した場合、その項目のみの選択にする
        const echartsInstance = echartsRef.current?.getEchartsInstance()
        if (echartsInstance) {
          const newSelectedState: { [key: string]: boolean } = {}
          Object.keys(selected).forEach(key => {
            newSelectedState[key] = key === name
          })
          echartsInstance.setOption({
            legend: {
              selected: newSelectedState
            }
          })
          return
        }
      }
      if (Object.values(selected).every(sel => sel === false)) {
        // 全解除したら、全選択に戻す
        const echartsInstance = echartsRef.current?.getEchartsInstance()
        if (echartsInstance) {
          const newSelectedState: { [key: string]: boolean } = {}
          Object.keys(selected).forEach(key => {
            newSelectedState[key] = true
          })
          echartsInstance.setOption({
            legend: {
              selected: newSelectedState
            }
          })
          return
        }
      }
    },
    []
  )
  if (!graph) return null
  return (
    <>
			<AuthorGraphSliderBar
				listCount={kojinList?.length ?? 100}
				limit={limit}
				setLimit={setLimit}
				sx={{
					position: 'absolute',
					bottom: 0, left: 0,
					paddingX: 2, paddingY: 1,
					zIndex: 10
				}}
			/>
			<AuthorGraphOptionBar
				showSingleNode={showSingleNode}
				setShowSingleNode={setShowSingleNode}
				graphType={graphType}
				setGraphType={setGraphType}
				category={category}
				setCategory={setCategory}
        symbolSizeRule={symbolSizeRule}
        setSymbolSizeRule={setSymbolSizeRule}
				sx={{
					position: 'absolute',
					top: 0, right: 0,
					paddingX: 2, paddingY: 2,
					zIndex: 10
				}}
			/>
      <ReactEChartsCore
        echarts={echarts}
        ref={echartsRef}
        notMerge={false}
        onEvents={{
          click: onClickGraph,
          legendselectchanged: onLegendSelectChanged
        }}
        style={{ height: '100%' }}
        option={option}
        opts={{
          renderer: 'svg',
          width: 'auto',
          height: 'auto'
        }}
        theme="insite"
      />
    </>
  )
}

export const AuthorGraph = memo(
  (props: AuthorGraphProps) => (
    <AuthorGraphCore {...props} />
  )
)
AuthorGraph.displayName = 'AuthorGraph'
